import { datadogRum } from "@datadog/browser-rum";
import { addReactError, reactPlugin } from "@datadog/browser-rum-react";
import type { ErrorInfo } from "react";
import {
  redactSensitiveKeys,
  sanitizeObservabilityText,
  sanitizeObservabilityUrl,
} from "@/lib/observability/redaction";
import { peekBackendConfig } from "@/lib/backend-config";
import { getDatadogEnv } from "./env";

const REDACTED = "[REDACTED]";
const AUTH_REFRESH_PATH = "/api/auth/refresh";
const AUTH_DENIED_STATUS_CODES = new Set<unknown>([401, 403]);

// `clearUser()` removes `usr.id` but Datadog retains an anonymous session ID.
// Keep this lifecycle signal so post-sign-out refresh denials remain visible.
let hasEstablishedRumUser = false;

type MutableRumRecord = Record<string, unknown>;

function asRecord(value: unknown): MutableRumRecord | null {
  return value && typeof value === "object" ? (value as MutableRumRecord) : null;
}

function getUrlEndpoint(value: unknown, base?: string): string | null {
  if (typeof value !== "string") {
    return null;
  }
  try {
    const url = new URL(value, base);
    return `${url.origin}${url.pathname}`;
  } catch {
    return null;
  }
}

function getConfiguredAuthRefreshEndpoint(): string | null {
  const backendConfig = peekBackendConfig();
  if (!backendConfig) {
    return null;
  }
  return getUrlEndpoint(AUTH_REFRESH_PATH, backendConfig.url);
}

function hasIdentifiedRumUser(event: MutableRumRecord): boolean {
  const user = asRecord(event.usr);
  return typeof user?.id === "string" && user.id.length > 0;
}

function getAnonymousResource(event: unknown): MutableRumRecord | null {
  const record = asRecord(event);
  if (record?.type !== "resource") {
    return null;
  }
  if (hasEstablishedRumUser || hasIdentifiedRumUser(record)) {
    return null;
  }
  return asRecord(record.resource);
}

function isDeniedAuthRefreshResource(
  resource: MutableRumRecord,
  configuredRefreshEndpoint: string,
): boolean {
  if (resource.method !== "POST") {
    return false;
  }
  if (!AUTH_DENIED_STATUS_CODES.has(resource.status_code)) {
    return false;
  }
  return getUrlEndpoint(resource.url) === configuredRefreshEndpoint;
}

/**
 * Password-session bootstrap must probe an HttpOnly cookie. InsForge answers
 * 401/403 when an anonymous browser has no cookie, and the auth layer correctly
 * maps that response to signed-out state rather than an application error.
 */
function isExpectedAnonymousAuthRefreshDenial(
  event: unknown,
  configuredRefreshEndpoint: string | null,
): boolean {
  if (!configuredRefreshEndpoint) {
    return false;
  }
  const resource = getAnonymousResource(event);
  if (!resource) {
    return false;
  }
  return isDeniedAuthRefreshResource(resource, configuredRefreshEndpoint);
}

function sanitizeRumMessage(event: MutableRumRecord): void {
  if (typeof event.message === "string") {
    event.message = sanitizeObservabilityText(event.message);
  }
}

function sanitizeRumContext(event: MutableRumRecord): void {
  const context = asRecord(event.context);
  if (context) {
    event.context = redactSensitiveKeys(context);
  }
}

function sanitizeRumView(event: MutableRumRecord): void {
  const view = asRecord(event.view);
  if (!view) {
    return;
  }
  if (typeof view.url === "string") {
    view.url = sanitizeObservabilityUrl(view.url);
  }
  if (typeof view.referrer === "string") {
    view.referrer = sanitizeObservabilityUrl(view.referrer);
  }
}

function sanitizeRumResource(event: MutableRumRecord): void {
  const resource = asRecord(event.resource);
  if (resource && typeof resource.url === "string") {
    resource.url = sanitizeObservabilityUrl(resource.url);
  }
}

function sanitizeRumAction(event: MutableRumRecord): void {
  const action = asRecord(event.action);
  const target = asRecord(action?.target);
  if (target && typeof target.name === "string") {
    target.name = REDACTED;
  }
}

function sanitizeRumError(event: MutableRumRecord): void {
  const error = asRecord(event.error);
  if (!error) {
    return;
  }
  if (typeof error.message === "string") {
    error.message = sanitizeObservabilityText(error.message);
  }
  if (typeof error.stack === "string") {
    error.stack = sanitizeObservabilityText(error.stack);
  }
}

function sanitizeRumEvent(event: unknown): void {
  const record = asRecord(event);
  if (!record) {
    return;
  }

  sanitizeRumMessage(record);
  sanitizeRumContext(record);
  sanitizeRumView(record);
  sanitizeRumResource(record);
  sanitizeRumAction(record);
  sanitizeRumError(record);
}

/** Returns whether required Datadog RUM env vars are present. */
export function isConfigured(): boolean {
  return getDatadogEnv() !== null;
}

/** Returns whether the Datadog RUM SDK has successfully initialized. */
export function isInitialized(): boolean {
  return datadogRum.getInitConfiguration() !== undefined;
}

/** Attaches the approved operator identity to Datadog RUM after initialization. */
export function setUser(user: {
  id: string;
  email?: string;
  name?: string;
  plan?: string;
  role?: string | null;
}): void {
  if (!(isConfigured() && isInitialized())) {
    return;
  }
  hasEstablishedRumUser = true;
  datadogRum.setUser({
    id: user.id,
    ...(user.role ? { role: user.role } : {}),
  });
}

/** Clears the Datadog RUM user when the operator signs out or auth is reset. */
export function clearUser(): void {
  if (!(isConfigured() && isInitialized())) {
    return;
  }
  datadogRum.clearUser();
}

/** Initializes browser RUM with privacy-safe event redaction when configured. */
export function init(): void {
  if (isInitialized()) {
    return;
  }

  const env = getDatadogEnv();
  if (!env) {
    console.info(
      "[datadog] not configured — set VITE_DATADOG_APPLICATION_ID and VITE_DATADOG_CLIENT_TOKEN to enable",
    );
    return;
  }

  const configuredAuthRefreshEndpoint = getConfiguredAuthRefreshEndpoint();

  datadogRum.init({
    applicationId: env.applicationId,
    clientToken: env.clientToken,
    site: env.site,
    service: env.service,
    env: env.env,
    version: env.version,
    // 100% of sessions are recorded; replay is sampled inside that.
    sessionSampleRate: 100,
    // 20% of sessions also capture session replay in prod; full in dev.
    sessionReplaySampleRate: env.env === "production" ? 20 : 100,
    defaultPrivacyLevel: "mask-user-input",
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    // Router integration requires a data router built by the SDK's wrapped
    // `createBrowserRouter`; this app renders declarative `<BrowserRouter>` +
    // `<Routes>`, so view names come from the URL instead.
    plugins: [reactPlugin({ router: false })],
    beforeSend: (event) => {
      if (isExpectedAnonymousAuthRefreshDenial(event, configuredAuthRefreshEndpoint)) {
        return false;
      }
      sanitizeRumEvent(event);
      return true;
    },
  });
}

/** Captures an exception through Datadog RUM when the SDK is active. */
export function captureException(err: unknown): void {
  if (!(isConfigured() && isInitialized())) {
    return;
  }
  datadogRum.addError(err);
}

/** Reports a React render error, with its component stack, to Datadog RUM. */
export function captureReactError(error: Error, errorInfo: ErrorInfo): void {
  if (!(isConfigured() && isInitialized())) {
    return;
  }
  addReactError(error, errorInfo);
}
